// SettingsPremium.jsx
import { useState, useEffect } from "react";
import "./SettingsPremium.css";
import axios from "axios";
import {
  FaBell,
  FaUser,
  FaLock,
  FaShieldAlt,
  FaTag,
  FaShoppingBag,
  FaWhatsapp,
  FaEnvelope,
  FaPhone,
  FaCalendarAlt,
  FaVenusMars,
  FaMapMarkerAlt,
  FaChevronDown,
  FaChevronUp,
  FaSpinner,
  FaCheckCircle,
  FaGlobe,
  FaMoon,
  FaLanguage,
} from "react-icons/fa";

const SettingsPremium = () => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // NOTIFICATION SETTINGS
  const [settings, setSettings] = useState({
    enableAll: false,
    offers: false,
    orders: false,
    whatsapp: false,
    email: false,
    push: false,
  });

  // User Details
  const [userDetails, setUserDetails] = useState({
    userName: "",
    emailId: "",
    mobileNumber: "",
    dateOfBirth: "",
    gender: "",
    address: "",
    image: "",
  });

  const [error, setError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Detect mobile view
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Toggle main sections
  const toggleDropdown = (section) => {
    setOpenDropdown(openDropdown === section ? null : section);
  };

  // Fetch User Info
  useEffect(() => {
    if (openDropdown !== "generalInfo") return;

    const userId = localStorage.getItem("userId");
    const token = localStorage.getItem("authToken");

    if (!userId) {
      setError("User not logged in.");
      setLoading(false);
      return;
    }

    const apiUrl = `https://backend.maamaas.com/subscription/api/user/getprofile/${userId}`;

    const fetchProfile = async () => {
      setLoading(true);
      try {
        const response = await fetch(apiUrl, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!response.ok) throw new Error("Failed to fetch profile");

        const data = await response.json();

        if (data.image) localStorage.setItem("profileImage", data.image);

        setUserDetails({
          userName: data.userName || "",
          emailId: data.emailId || "",
          mobileNumber: data.mobileNumber || "",
          dateOfBirth: data.dateOfBirth || "",
          gender: data.gender || "",
          address: data.address || "",
          image: data.image || localStorage.getItem("profileImage") || "",
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [openDropdown]);

  // Handle Toggle Switches
  const toggleSetting = (key) => {
    if (key === "enableAll") {
      const newValue = !settings.enableAll;
      setSettings({
        enableAll: newValue,
        offers: newValue,
        orders: newValue,
        whatsapp: newValue,
        email: newValue,
        push: newValue,
      });
    } else {
      const updated = { ...settings, [key]: !settings[key] };

      const allEnabled =
        updated.offers && updated.orders && updated.whatsapp && 
        updated.email && updated.push;

      setSettings({
        ...updated,
        enableAll: allEnabled,
      });
    }
  };

  // Handle Save Settings
  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      const userId = localStorage.getItem("userId");
      const token = localStorage.getItem("authToken");

      const response = await axios.post(
        `https://backend.maamaas.com/subscription/api/user/update-settings/${userId}`,
        settings,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );

      if (response.status === 200) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (err) {
      setError("Failed to save settings");
      setTimeout(() => setError(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  const sections = [
    {
      id: "notifications",
      icon: <FaBell />,
      title: "Notifications & Preferences",
      description: "Manage your notification settings",
      color: "#3498db",
    },
    {
      id: "generalInfo",
      icon: <FaUser />,
      title: "General Information",
      description: "View your account details",
      color: "#2ecc71",
    },
    {
      id: "privacy",
      icon: <FaLock />,
      title: "Privacy & Security",
      description: "Manage your privacy preferences",
      color: "#e74c3c",
    },
    {
      id: "preferences",
      icon: <FaGlobe />,
      title: "App Preferences",
      description: "Language, theme and more",
      color: "#f39c12",
    },
  ];

  return (
    <div className="premiumSettings-root">
      <div className="premiumSettings-container">
        {/* Header */}
        <div className="premiumSettings-header">
          <div className="premiumSettings-headerContent">
            <h1 className="premiumSettings-title">Settings</h1>
            <p className="premiumSettings-subtitle">
              Manage your account preferences
            </p>
          </div>
        </div>

        {/* Success Message */}
        {saveSuccess && (
          <div className="premiumSettings-successMsg">
            <FaCheckCircle />
            <span>Settings saved successfully!</span>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="premiumSettings-errorMsg">
            <span>{error}</span>
          </div>
        )}

        {/* Settings Sections */}
        <div className="premiumSettings-sections">
          {sections.map((section) => (
            <div key={section.id} className="premiumSettings-section">
              <button
                onClick={() => toggleDropdown(section.id)}
                className="premiumSettings-dropdownBtn"
                style={{ borderLeftColor: section.color }}
              >
                <div className="premiumSettings-btnContent">
                  <div className="premiumSettings-btnLeft">
                    <div
                      className="premiumSettings-btnIcon"
                      style={{ background: `${section.color}15`, color: section.color }}
                    >
                      {section.icon}
                    </div>
                    <div className="premiumSettings-btnText">
                      <h3 className="premiumSettings-btnTitle">{section.title}</h3>
                      <p className="premiumSettings-btnDesc">{section.description}</p>
                    </div>
                  </div>
                  <div className="premiumSettings-btnArrow">
                    {openDropdown === section.id ? <FaChevronUp /> : <FaChevronDown />}
                  </div>
                </div>
              </button>

              {openDropdown === section.id && (
                <div className="premiumSettings-dropdownContent">
                  {/* Notifications Section */}
                  {section.id === "notifications" && (
                    <div className="premiumSettings-notifications">
                      <div className="premiumSettings-notifHeader">
                        <h4>Notification Channels</h4>
                        <p>Choose how you want to receive updates</p>
                      </div>

                      <div className="premiumSettings-settingsList">
                        {/* Enable All */}
                        <div className="premiumSettings-settingItem">
                          <div className="premiumSettings-settingInfo">
                            <h5>Enable All Notifications</h5>
                            <p>Turn on all notification types</p>
                          </div>
                          <label className="premiumSettings-toggle">
                            <input
                              type="checkbox"
                              checked={settings.enableAll}
                              onChange={() => toggleSetting("enableAll")}
                            />
                            <span className="premiumSettings-toggleSlider"></span>
                          </label>
                        </div>

                        {/* Offers */}
                        <div className="premiumSettings-settingItem">
                          <div className="premiumSettings-settingInfo">
                            <div className="premiumSettings-settingIcon">
                              <FaTag />
                            </div>
                            <div>
                              <h5>Offers and Coupons</h5>
                              <p>Receive special offers and discount coupons</p>
                            </div>
                          </div>
                          <label className="premiumSettings-toggle">
                            <input
                              type="checkbox"
                              checked={settings.offers}
                              disabled={settings.enableAll}
                              onChange={() => toggleSetting("offers")}
                            />
                            <span className="premiumSettings-toggleSlider"></span>
                          </label>
                        </div>

                        {/* Orders */}
                        <div className="premiumSettings-settingItem">
                          <div className="premiumSettings-settingInfo">
                            <div className="premiumSettings-settingIcon">
                              <FaShoppingBag />
                            </div>
                            <div>
                              <h5>Order Updates</h5>
                              <p>Get notified about your orders and purchases</p>
                            </div>
                          </div>
                          <label className="premiumSettings-toggle">
                            <input
                              type="checkbox"
                              checked={settings.orders}
                              disabled={settings.enableAll}
                              onChange={() => toggleSetting("orders")}
                            />
                            <span className="premiumSettings-toggleSlider"></span>
                          </label>
                        </div>

                        {/* WhatsApp */}
                        <div className="premiumSettings-settingItem">
                          <div className="premiumSettings-settingInfo">
                            <div className="premiumSettings-settingIcon">
                              <FaWhatsapp />
                            </div>
                            <div>
                              <h5>WhatsApp Notifications</h5>
                              <p>Receive updates via WhatsApp</p>
                            </div>
                          </div>
                          <label className="premiumSettings-toggle">
                            <input
                              type="checkbox"
                              checked={settings.whatsapp}
                              disabled={settings.enableAll}
                              onChange={() => toggleSetting("whatsapp")}
                            />
                            <span className="premiumSettings-toggleSlider"></span>
                          </label>
                        </div>

                        {/* Email */}
                        <div className="premiumSettings-settingItem">
                          <div className="premiumSettings-settingInfo">
                            <div className="premiumSettings-settingIcon">
                              <FaEnvelope />
                            </div>
                            <div>
                              <h5>Email Notifications</h5>
                              <p>Receive updates via email</p>
                            </div>
                          </div>
                          <label className="premiumSettings-toggle">
                            <input
                              type="checkbox"
                              checked={settings.email}
                              disabled={settings.enableAll}
                              onChange={() => toggleSetting("email")}
                            />
                            <span className="premiumSettings-toggleSlider"></span>
                          </label>
                        </div>
                      </div>

                      <button
                        className="premiumSettings-saveBtn"
                        onClick={handleSaveSettings}
                        disabled={loading}
                      >
                        {loading ? <FaSpinner className="spinner" /> : "Save Preferences"}
                      </button>
                    </div>
                  )}

                  {/* General Information Section */}
                  {section.id === "generalInfo" && (
                    <div className="premiumSettings-generalInfo">
                      {loading ? (
                        <div className="premiumSettings-loading">
                          <FaSpinner className="premiumSettings-spinner" />
                          <p>Loading your information...</p>
                        </div>
                      ) : error ? (
                        <div className="premiumSettings-error">{error}</div>
                      ) : (
                        <div className="premiumSettings-infoGrid">
                          <div className="premiumSettings-infoCard">
                            <div className="premiumSettings-infoIcon">
                              <FaUser />
                            </div>
                            <div className="premiumSettings-infoContent">
                              <span className="premiumSettings-infoLabel">Full Name</span>
                              <span className="premiumSettings-infoValue">{userDetails.userName || "—"}</span>
                            </div>
                          </div>

                          <div className="premiumSettings-infoCard">
                            <div className="premiumSettings-infoIcon">
                              <FaEnvelope />
                            </div>
                            <div className="premiumSettings-infoContent">
                              <span className="premiumSettings-infoLabel">Email Address</span>
                              <span className="premiumSettings-infoValue">{userDetails.emailId || "—"}</span>
                            </div>
                          </div>

                          <div className="premiumSettings-infoCard">
                            <div className="premiumSettings-infoIcon">
                              <FaPhone />
                            </div>
                            <div className="premiumSettings-infoContent">
                              <span className="premiumSettings-infoLabel">Phone Number</span>
                              <span className="premiumSettings-infoValue">{userDetails.mobileNumber || "—"}</span>
                            </div>
                          </div>

                          <div className="premiumSettings-infoCard">
                            <div className="premiumSettings-infoIcon">
                              <FaCalendarAlt />
                            </div>
                            <div className="premiumSettings-infoContent">
                              <span className="premiumSettings-infoLabel">Date of Birth</span>
                              <span className="premiumSettings-infoValue">{userDetails.dateOfBirth || "—"}</span>
                            </div>
                          </div>

                          <div className="premiumSettings-infoCard">
                            <div className="premiumSettings-infoIcon">
                              <FaVenusMars />
                            </div>
                            <div className="premiumSettings-infoContent">
                              <span className="premiumSettings-infoLabel">Gender</span>
                              <span className="premiumSettings-infoValue">{userDetails.gender || "—"}</span>
                            </div>
                          </div>

                          <div className="premiumSettings-infoCard">
                            <div className="premiumSettings-infoIcon">
                              <FaMapMarkerAlt />
                            </div>
                            <div className="premiumSettings-infoContent">
                              <span className="premiumSettings-infoLabel">Address</span>
                              <span className="premiumSettings-infoValue">{userDetails.address || "—"}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Privacy Section */}
                  {section.id === "privacy" && (
                    <div className="premiumSettings-privacy">
                      <div className="premiumSettings-privacyCard">
                        <FaShieldAlt className="premiumSettings-privacyIcon" />
                        <h4>Account Privacy</h4>
                        <p>Your data is encrypted and secure. We follow strict privacy policies to protect your information.</p>
                        <button className="premiumSettings-privacyBtn">Manage Privacy</button>
                      </div>
                    </div>
                  )}

                  {/* Preferences Section */}
                  {section.id === "preferences" && (
                    <div className="premiumSettings-preferences">
                      <div className="premiumSettings-prefItem">
                        <div className="premiumSettings-prefInfo">
                          <FaLanguage />
                          <div>
                            <h5>Language</h5>
                            <p>Choose your preferred language</p>
                          </div>
                        </div>
                        <select className="premiumSettings-select">
                          <option>English</option>
                          <option>Hindi</option>
                          <option>Spanish</option>
                        </select>
                      </div>

                      <div className="premiumSettings-prefItem">
                        <div className="premiumSettings-prefInfo">
                          <FaMoon />
                          <div>
                            <h5>Dark Mode</h5>
                            <p>Switch between light and dark theme</p>
                          </div>
                        </div>
                        <label className="premiumSettings-toggle">
                          <input type="checkbox" />
                          <span className="premiumSettings-toggleSlider"></span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

       
        
      </div>
    </div>
  );
};

export default SettingsPremium;